/**
 * Environment Configuration
 *
 * Centralized access to environment variables with type safety and validation.
 * All environment variables must be prefixed with VITE_ to be exposed to the client.
 *
 * @see https://vitejs.dev/guide/env-and-mode.html
 */

// API Configuration
export const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_URL || 'https://api.tereiko.com/api/v1',
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10),
} as const;

// Google SSO Configuration
export const GOOGLE_CONFIG = {
  clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
} as const;

// Application Configuration
export const APP_CONFIG = {
  name: import.meta.env.VITE_APP_NAME || 'Tereiko LMS',
  url: import.meta.env.VITE_APP_URL || 'http://localhost:3000',
} as const;

// Feature Flags
export const FEATURES = {
  darkMode: import.meta.env.VITE_ENABLE_DARK_MODE === 'true',
  analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
} as const;

// Debug Configuration
export const DEBUG = {
  enabled: import.meta.env.VITE_DEBUG === 'true',
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  mode: import.meta.env.MODE,
} as const;

// Environment validation
const validateEnv = (): void => {
  const errors: string[] = [];

  // Check required environment variables
  if (!API_CONFIG.baseURL) {
    errors.push('VITE_API_URL is required');
  }

  // Warn about missing Google Client ID (not required for basic functionality)
  if (!GOOGLE_CONFIG.clientId && import.meta.env.PROD) {
    console.warn('VITE_GOOGLE_CLIENT_ID is not set. Google SSO will not work.');
  }

  // Throw error if critical variables are missing
  if (errors.length > 0) {
    throw new Error(`Environment configuration errors:\n${errors.join('\n')}`);
  }
};

// Validate environment on module load (only in development)
if (import.meta.env.DEV) {
  validateEnv();
}

// Export all configurations
export const ENV = {
  api: API_CONFIG,
  google: GOOGLE_CONFIG,
  app: APP_CONFIG,
  features: FEATURES,
  debug: DEBUG,
} as const;

export default ENV;
